import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  approveCalibrationPortrait,
  deleteClientPhoto,
  generateCalibrationPortrait,
  getClient,
  getJob,
  listClientPhotos,
  photoPublicUrl,
  rejectCalibrationPortrait,
  tagPhotoBracket,
  uploadClientPhotos,
} from "../../api/bd";
import { useAuthImage } from "@/hooks/useAuthImage";

const BRACKETS = ["enfant", "jeune_adulte", "adulte"];
const BRACKET_LABELS = {
  enfant: "🧒 Enfant",
  jeune_adulte: "🧑 Jeune adulte",
  adulte: "🧔 Adulte",
};

function normalizePortraits(value) {
  return Object.fromEntries(
    BRACKETS.map((bracket) => [
      bracket,
      Array.isArray(value?.[bracket]) ? [...value[bracket]] : [],
    ]),
  );
}

function matchesPhoto(url, filename) {
  if (typeof url !== "string") return false;
  const suffix = `/photos/${filename}`;
  try {
    return decodeURIComponent(new URL(url, window.location.origin).pathname).endsWith(suffix);
  } catch (_error) {
    return url.endsWith(suffix);
  }
}

function CalibrationCandidateImage({ token, candidate, bracket }) {
  const { src, error } = useAuthImage(candidate?.image_url, token);
  if (error) {
    return (
      <div className="flex aspect-square items-center justify-center bg-red-50 px-3 text-center text-xs text-red-700">
        Impossible de charger le portrait candidat.
      </div>
    );
  }
  if (!src) return <div className="aspect-square animate-pulse bg-slate-100" />;
  return (
    <img
      src={src}
      alt={`Portrait candidat — ${BRACKET_LABELS[bracket]}`}
      className="aspect-square h-full w-full object-cover"
    />
  );
}

function ApprovedPortraitThumb({ token, url, bracket, onRemove, disabled }) {
  const { src, error } = useAuthImage(url, token);
  return (
    <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded border border-emerald-300 bg-white">
      {error ? (
        <div className="flex h-full items-center justify-center text-center text-[10px] text-red-700">
          Erreur
        </div>
      ) : !src ? (
        <div className="h-full w-full animate-pulse bg-slate-100" />
      ) : (
        <img
          src={src}
          alt={`Tenue approuvée — ${BRACKET_LABELS[bracket]}`}
          className="h-full w-full object-cover"
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        title="Retirer cette tenue"
        aria-label="Retirer cette tenue"
        className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-bl bg-black/60 text-[10px] text-white hover:bg-black/80 disabled:opacity-50"
      >
        ×
      </button>
    </div>
  );
}

export default function BDPhotosPanel({
  token,
  clientId,
  photoRefs = [],
  portraitsByBracket,
  calibrationPending,
  chapters = [],
  onPhotoRefsChange,
  onPortraitsChange,
}) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [portraits, setPortraits] = useState(() => normalizePortraits(portraitsByBracket));
  const [candidates, setCandidates] = useState(() => ({ ...(calibrationPending || {}) }));
  const [styleNotes, setStyleNotes] = useState(() => Object.fromEntries(
    BRACKETS.map((bracket) => [bracket, calibrationPending?.[bracket]?.style_note || ""]),
  ));
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(null);
  const [calibrationBusy, setCalibrationBusy] = useState(null);
  const [pendingBracket, setPendingBracket] = useState(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const savingRef = useRef(false);

  useEffect(() => {
    setPortraits(normalizePortraits(portraitsByBracket));
  }, [portraitsByBracket]);

  useEffect(() => {
    setCandidates({ ...(calibrationPending || {}) });
    setStyleNotes((current) => Object.fromEntries(
      BRACKETS.map((bracket) => [
        bracket,
        calibrationPending?.[bracket]?.style_note ?? current[bracket] ?? "",
      ]),
    ));
  }, [calibrationPending]);

  const detectedBrackets = useMemo(() => {
    const detected = new Set(chapters.map((chapter) => chapter.age_bracket).filter(Boolean));
    return BRACKETS.filter((bracket) => detected.has(bracket));
  }, [chapters]);

  const refresh = async () => {
    setLoading(true);
    try {
      const d = await listClientPhotos(token, clientId);
      setPhotos(d.photos || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [clientId]);

  useEffect(() => {
    setPendingBracket(null);
    setCalibrationBusy(null);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [clientId]);

  const storedUrlForPhoto = (filename) => (
    photoRefs.find((url) => matchesPhoto(url, filename))
    || photoPublicUrl(clientId, filename)
  );

  const bracketForPhoto = (url) => (
    BRACKETS.find((bracket) => portraits[bracket].includes(url)) || ""
  );

  const recoverPortraits = async () => {
    try {
      const client = await getClient(token, clientId);
      const next = normalizePortraits(client.character?.portraits_by_bracket);
      const nextPhotoRefs = Array.isArray(client.character?.photos_refs)
        ? [...client.character.photos_refs]
        : [];
      setPortraits(next);
      setCandidates({ ...(client.character?.calibration_pending || {}) });
      onPhotoRefsChange?.(nextPhotoRefs);
      onPortraitsChange?.(next);
    } catch (error) {
      console.error(error);
    }
  };

  const removeCandidate = (bracket) => {
    setCandidates((current) => {
      const next = { ...current };
      delete next[bracket];
      return next;
    });
  };

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const pollCalibrationJob = (bracket) => {
    stopPolling();
    const POLL_FAIL_MAX = 5;
    let consecutiveFails = 0;
    pollRef.current = setInterval(async () => {
      try {
        const job = await getJob(token, clientId);
        consecutiveFails = 0;
        if (job.status === "done") {
          stopPolling();
          setPendingBracket(null);
          await recoverPortraits();
          toast.success("Portrait candidat généré");
        } else if (job.status === "error") {
          stopPolling();
          setPendingBracket(null);
          toast.error(job.error || "Échec de la génération du candidat.");
        }
      } catch (_error) {
        consecutiveFails += 1;
        if (consecutiveFails >= POLL_FAIL_MAX) {
          stopPolling();
          setPendingBracket(null);
          toast.error(
            "Connexion instable — recharge la page pour vérifier le résultat."
          );
        }
      }
    }, 4000);
  };

  const generateCandidate = async (bracket) => {
    if (calibrationBusy || pendingBracket) return;
    setCalibrationBusy(bracket);
    try {
      await generateCalibrationPortrait(
        token,
        clientId,
        bracket,
        styleNotes[bracket],
      );
      setPendingBracket(bracket);
      pollCalibrationJob(bracket);
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.warning(
          "Une génération est déjà en cours pour ce client, patiente qu’elle se termine."
        );
        setPendingBracket(bracket);
        pollCalibrationJob(bracket);
      } else {
        console.error(error);
        toast.error(error?.response?.data?.detail || "Erreur de génération du candidat");
      }
    } finally {
      setCalibrationBusy(null);
    }
  };

  const approveCandidate = async (bracket) => {
    if (calibrationBusy || pendingBracket) return;
    setCalibrationBusy(bracket);
    try {
      const result = await approveCalibrationPortrait(token, clientId, bracket);
      const next = normalizePortraits(portraits);
      next[bracket] = Array.isArray(result.portraits) ? [...result.portraits] : [];
      setPortraits(next);
      removeCandidate(bracket);
      onPortraitsChange?.(next);
      toast.success("Portrait candidat approuvé");
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || "Erreur d’approbation du candidat");
    } finally {
      setCalibrationBusy(null);
    }
  };

  const rejectCandidate = async (bracket) => {
    if (calibrationBusy || pendingBracket) return;
    setCalibrationBusy(bracket);
    try {
      await rejectCalibrationPortrait(token, clientId, bracket);
      removeCandidate(bracket);
      toast.success("Portrait candidat rejeté");
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || "Erreur de rejet du candidat");
    } finally {
      setCalibrationBusy(null);
    }
  };

  const removePortrait = async (bracket, urlToRemove) => {
    if (calibrationBusy || pendingBracket) return;
    const nextBracketPortraits = portraits[bracket].filter((url) => url !== urlToRemove);
    setCalibrationBusy(bracket);
    try {
      await tagPhotoBracket(token, clientId, bracket, nextBracketPortraits);
      const next = normalizePortraits(portraits);
      next[bracket] = nextBracketPortraits;
      setPortraits(next);
      onPortraitsChange?.(next);
      toast.success("Tenue retirée.");
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Erreur lors du retrait de la tenue.");
    } finally {
      setCalibrationBusy(null);
    }
  };

  const onBracketChange = async (url, nextBracket) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSavingPhoto(url);

    const next = normalizePortraits(portraits);
    const changed = [];

    for (const bracket of BRACKETS) {
      const filtered = next[bracket].filter((candidate) => candidate !== url);
      if (filtered.length !== next[bracket].length) {
        next[bracket] = filtered;
        changed.push(bracket);
      }
    }
    if (nextBracket && !next[nextBracket].includes(url)) {
      next[nextBracket] = [...next[nextBracket], url];
      if (!changed.includes(nextBracket)) changed.push(nextBracket);
    }

    try {
      await Promise.all(
        changed.map((bracket) => (
          tagPhotoBracket(token, clientId, bracket, next[bracket])
        )),
      );
      setPortraits(next);
      onPortraitsChange?.(next);
      toast.success(nextBracket ? "Tranche d’âge enregistrée" : "Tranche d’âge retirée");
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || "Erreur de calibrage");
      await recoverPortraits();
    } finally {
      savingRef.current = false;
      setSavingPhoto(null);
    }
  };

  const onUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      await uploadClientPhotos(token, clientId, Array.from(files));
      toast.success(`${files.length} photo(s) uploadée(s)`);
      await refresh();
      await recoverPortraits();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDelete = async (filename) => {
    if (!window.confirm(`Supprimer ${filename} ?`)) return;
    try {
      await deleteClientPhoto(token, clientId, filename);
      toast.success("Photo supprimée");
      await refresh();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copiée — colle-la dans le chat pour setup_character");
    } catch (e) {
      toast.error("Impossible de copier");
    }
  };

  const copyAll = async () => {
    if (photos.length === 0) return;
    const urls = photos.map((p) => photoPublicUrl(clientId, p.filename)).join("\n");
    try {
      await navigator.clipboard.writeText(urls);
      toast.success(`${photos.length} URLs copiées`);
    } catch (e) {
      toast.error("Impossible de copier");
    }
  };

  return (
    <div className="border-b bg-gray-50">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-6 py-2 flex items-center justify-between text-sm hover:bg-gray-100 transition"
      >
        <span className="font-medium">
          📸 Photos de référence ({photos.length})
        </span>
        <span className="text-gray-500">{open ? "▲ Fermer" : "▼ Ouvrir"}</span>
      </button>

      {open && (
        <div className="px-6 py-4 space-y-3 border-t bg-white">
          {/* Upload zone */}
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              // image/* (et non des extensions) pour que l'appareil photo
              // s'affiche sur Android ; le backend valide les magic bytes.
              accept="image/*"
              multiple
              onChange={(e) => onUpload(e.target.files)}
              className="text-sm"
              disabled={uploading}
            />
            {uploading && <span className="text-xs text-gray-500">Upload…</span>}
          </div>
          <p className="text-xs text-gray-500">
            Formats : .jpg .jpeg .png .webp · Max 12 Mo/photo · Plusieurs à la fois OK
          </p>

          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-xs font-medium text-slate-700">
              Tranches d’âge détectées dans les chapitres :
            </p>
            {detectedBrackets.length === 0 ? (
              <p className="mt-1 text-xs text-slate-500">Aucune pour le moment.</p>
            ) : (
              <div className="mt-2 space-y-2">
                {detectedBrackets.map((bracket) => {
                  const missing = portraits[bracket].length === 0;
                  const candidate = candidates[bracket];
                  const busy = calibrationBusy === bracket || pendingBracket === bracket;
                  const calibrationLocked = Boolean(calibrationBusy || pendingBracket);
                  return (
                    <div
                      key={bracket}
                      className="space-y-2"
                    >
                      <span
                        className={`inline-block rounded-full border px-2 py-0.5 text-xs ${
                          missing
                            ? "border-amber-200 bg-amber-50 text-amber-800"
                            : "border-emerald-200 bg-emerald-50 text-emerald-800"
                        }`}
                        title={missing ? "Aucune photo calibrée pour cette tranche" : "Photo calibrée"}
                      >
                        {missing ? "⚠️ " : "✓ "}{BRACKET_LABELS[bracket]}
                      </span>

                      {portraits[bracket].length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {portraits[bracket].map((url) => (
                            <ApprovedPortraitThumb
                              key={url}
                              token={token}
                              url={url}
                              bracket={bracket}
                              disabled={calibrationLocked}
                              onRemove={() => removePortrait(bracket, url)}
                            />
                          ))}
                        </div>
                      )}

                      {!candidate && (
                        <div className="flex flex-col gap-2 rounded border border-amber-200 bg-white p-2 sm:flex-row sm:items-end">
                          <label className="min-w-0 flex-1">
                            <span className="mb-1 block text-xs font-medium text-slate-600">
                              Note de style optionnelle (tenue, époque, décor)
                            </span>
                            <input
                              type="text"
                              value={styleNotes[bracket] || ""}
                              onChange={(event) => setStyleNotes((current) => ({
                                ...current,
                                [bracket]: event.target.value,
                              }))}
                              placeholder="Ex. tenue d’enfant années 1980, jardin"
                              aria-label={`Note de style pour ${BRACKET_LABELS[bracket]}`}
                              disabled={calibrationLocked}
                              className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200 disabled:opacity-60"
                            />
                          </label>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => generateCandidate(bracket)}
                              disabled={calibrationLocked}
                              className="rounded bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700 disabled:cursor-wait disabled:opacity-60"
                            >
                              {busy
                                ? "Génération…"
                                : portraits[bracket].length === 0
                                  ? "🎨 Générer un candidat"
                                  : "🎨 Ajouter une autre tenue"}
                            </button>
                            <span className="whitespace-nowrap text-xs font-medium text-amber-800">
                              ≈0,17 €
                            </span>
                          </div>
                        </div>
                      )}

                      {candidate && (
                        <div className="overflow-hidden rounded border border-amber-300 bg-amber-50 text-sm sm:flex">
                          <div className="w-full shrink-0 bg-white sm:w-40">
                            <CalibrationCandidateImage
                              token={token}
                              candidate={candidate}
                              bracket={bracket}
                            />
                          </div>
                          <div className="flex-1 space-y-2 p-3">
                            <div>
                              <p className="font-semibold text-amber-950">Portrait candidat à vérifier</p>
                              {candidate.style_note && (
                                <p className="text-xs text-amber-800">Style : {candidate.style_note}</p>
                              )}
                            </div>
                            <label className="block">
                              <span className="mb-1 block text-xs font-medium text-slate-600">
                                Nouvelle note pour régénérer (optionnelle)
                              </span>
                              <input
                                type="text"
                                value={styleNotes[bracket] || ""}
                                onChange={(event) => setStyleNotes((current) => ({
                                  ...current,
                                  [bracket]: event.target.value,
                                }))}
                                aria-label={`Nouvelle note de style pour ${BRACKET_LABELS[bracket]}`}
                                disabled={calibrationLocked}
                                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200 disabled:opacity-60"
                              />
                            </label>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => approveCandidate(bracket)}
                                disabled={calibrationLocked}
                                className="rounded bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:cursor-wait disabled:opacity-60"
                              >
                                ✓ Approuver
                              </button>
                              <button
                                type="button"
                                onClick={() => generateCandidate(bracket)}
                                disabled={calibrationLocked}
                                className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-wait disabled:opacity-60"
                              >
                                {busy ? "Génération…" : "🔄 Régénérer · ≈0,17 €"}
                              </button>
                              <button
                                type="button"
                                onClick={() => rejectCandidate(bracket)}
                                disabled={calibrationLocked}
                                className="rounded bg-gray-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:cursor-wait disabled:opacity-60"
                              >
                                ✕ Rejeter
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Liste */}
          {loading ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : photos.length === 0 ? (
            <p className="text-sm text-gray-500">
              Aucune photo pour ce client. Uploade-en ci-dessus, puis copie les URLs et colle-les dans le chat pour le <code className="bg-gray-100 px-1 rounded">setup_character</code>.
            </p>
          ) : (
            <>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={copyAll}
                  className="text-xs px-2 py-1 bg-orange-600 text-white rounded hover:bg-orange-700"
                >
                  Copier toutes les URLs ({photos.length})
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {photos.map((p) => {
                  const url = photoPublicUrl(clientId, p.filename);
                  const storedUrl = storedUrlForPhoto(p.filename);
                  const selectedBracket = bracketForPhoto(storedUrl);
                  return (
                    <div key={p.filename} className="border rounded overflow-hidden bg-white">
                      <div className="aspect-square bg-gray-100 flex items-center justify-center">
                        <img
                          src={url}
                          alt={p.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-2 space-y-1">
                        <p className="text-xs truncate" title={p.filename}>{p.filename}</p>
                        <label className="block">
                          <span className="sr-only">Tranche d’âge pour {p.filename}</span>
                          <select
                            value={selectedBracket}
                            onChange={(event) => onBracketChange(storedUrl, event.target.value)}
                            disabled={savingPhoto !== null}
                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200 disabled:cursor-wait disabled:opacity-60"
                            aria-label={`Tranche d’âge pour ${p.filename}`}
                          >
                            <option value="">— Aucune tranche</option>
                            {BRACKETS.map((bracket) => (
                              <option key={bracket} value={bracket}>
                                {BRACKET_LABELS[bracket]}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => copyUrl(url)}
                            className="flex-1 text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                          >
                            Copier URL
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(p.filename)}
                            className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded"
                            title="Supprimer"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
